import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { isNoneColor } from '@src/utility/color';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { type RgbaColor, RgbaColorPicker } from 'react-colorful';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';

import CheckIcon from '../../svgs/CheckIcon';
import DeleteIcon from '../../svgs/DeleteIcon';
import EyeDropperIcon from '../../svgs/EyeDropperIcon';

import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

// Non-standard EyeDropper API ambient declarations for safer usage without any-casts.
interface EyeDropperResult {
  sRGBHex: string;
}
interface EyeDropper {
  open(): Promise<EyeDropperResult>;
}
declare global {
  interface Window {
    EyeDropper?: { new (): EyeDropper };
  }
}

const logger = LogManager.getLogger('components:ColorPicker');

type Props = {
  pos: DOMRect;
  action?: (propertyMap: Partial<KaraokeConfig>) => void;
  propertyName?: string;
  defaultValue?: string | null;
  getValue: (color: string | null, isManualEditing?: boolean) => void;
  onClose: () => void;
  solidDefaultAlpha?: boolean;
  tab?: ColorTabs;
  onPreview?: (color: string | null) => void;
};

export enum ColorTabs {
  preset = 'Presets',
  custom = 'Custom',
}

// 30 colors arranged row-first: dark → medium → light, 10 per row.
// Each row shares the same shade level so columns read as color families.
const PRESET_COLORS: string[] = [
  // Row 1 — dark shades
  'rgba(0, 0, 0, 1)',
  'rgba(183, 28, 28, 1)',
  'rgba(230, 81, 0, 1)',
  'rgba(245, 127, 23, 1)',
  'rgba(25, 77, 51, 1)',
  'rgba(27, 94, 32, 1)',
  'rgba(0, 96, 100, 1)',
  'rgba(13, 71, 161, 1)',
  'rgba(136, 14, 79, 1)',
  'rgba(74, 20, 140, 1)',
  // Row 2 — medium shades
  'rgba(150, 150, 150, 1)',
  'rgba(244, 67, 54, 1)',
  'rgba(255, 87, 34, 1)',
  'rgba(251, 192, 45, 1)',
  'rgba(76, 175, 80, 1)',
  'rgba(139, 195, 74, 1)',
  'rgba(0, 188, 212, 1)',
  'rgba(33, 150, 243, 1)',
  'rgba(233, 30, 99, 1)',
  'rgba(156, 39, 176, 1)',
  // Row 3 — light shades
  'rgba(255, 255, 255, 1)',
  'rgba(255, 205, 210, 1)',
  'rgba(255, 204, 188, 1)',
  'rgba(255, 241, 118, 1)',
  'rgba(200, 230, 201, 1)',
  'rgba(220, 237, 200, 1)',
  'rgba(178, 235, 242, 1)',
  'rgba(187, 222, 251, 1)',
  'rgba(248, 187, 208, 1)',
  'rgba(225, 190, 231, 1)',
];

const DefaultColors = {
  white: 'rgba(255, 255, 255, 1)',
  black: 'rgba(0, 0, 0, 1)',
  red: 'rgba(255, 0, 0, 1)',
  green: 'rgba(0, 255, 0, 1)',
  orange: 'rgba(255, 165, 0, 1)',
  blue: 'rgba(0, 0, 255, 1)',
  yellow: 'rgba(255, 255, 0, 1)',
};

function hexToRgba(hex: string, alpha?: number) {
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  } else if (hex.length === 9) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
    alpha = parseInt(hex.slice(7, 9), 16) / 255;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'decimal',
      maximumFractionDigits: 3,
      minimumFractionDigits: 0,
    });
    let a = formatter.format(alpha);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  } else {
    logger.warn('Invalid hex color code passed to hexToRgba', { hex });
    return;
  }
  alpha = typeof alpha !== 'undefined' ? alpha : 1;
  alpha = Math.min(Math.max(0, alpha), 1);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

//TODO: Shouldn't need to pass videoCreator as a parameter
function stringToRgba(videoCreator: IVideoCreatorStore, color: string) {
  const [r, g, b, a] = videoCreator.textBrandingService
    .splitRgbaString(color)
    .map(Number);
  return {
    r,
    g,
    b,
    a: a * 100,
  };
}

function isDefaultColorKey(v: string): v is keyof typeof DefaultColors {
  return Object.prototype.hasOwnProperty.call(DefaultColors, v);
}
function getDefaultRgba(value: string | undefined | null): string {
  if (!value) return '';
  const lower = value.toLowerCase();
  let rgba: string;
  if (lower.includes('rgb')) rgba = lower;
  else if (isDefaultColorKey(lower)) rgba = DefaultColors[lower];
  // Hex string (e.g. "#B71C1C") — convert to rgba so downstream logic works
  else if (lower.startsWith('#')) rgba = hexToRgba(lower) ?? '';
  else return '';
  // A fully-transparent color (alpha 0) is the erased "no color" state. It must
  // resolve to no selection — otherwise the picker pre-selects black, because
  // transparent black shares its RGB channels with the black preset
  // (isMatchingPreset ignores alpha) and converts to #000000 in the hex input.
  return isNoneColor(rgba) ? '' : rgba;
}

function getDefaultAlpha(rgba: string | null | undefined) {
  if (!rgba) return '1';
  const rgbaValues = rgba
    .substring(5, rgba.length - 1)
    .split(',')
    .map(value => value.trim());

  if (rgbaValues.length === 4) {
    const alpha = rgbaValues[3];
    if (!alpha || alpha === 'NaN') return '1';
    return alpha;
  }
}

function rgbaToString(rgba: RgbaColor) {
  return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a / 100})`;
}

function isLightColor(rgba: string): boolean {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return false;
  const luminance =
    0.2126 * parseInt(match[1]) +
    0.7152 * parseInt(match[2]) +
    0.0722 * parseInt(match[3]);
  return luminance > 170;
}

function isMatchingPreset(
  videoCreator: IVideoCreatorStore,
  color: string,
  rgbaColor: RgbaColor,
  preset: string,
) {
  const rgbaPreset = stringToRgba(videoCreator, preset);
  return (
    color === preset ||
    (rgbaColor.r === rgbaPreset.r &&
      rgbaColor.g === rgbaPreset.g &&
      rgbaColor.b === rgbaPreset.b)
  );
}

const WIDTH = 270;
const MARGIN = 8;

type Placement = { top: number; left: number; maxHeight?: number };

/**
 * Always anchors directly below the trigger. maxHeight clamps the picker
 * to the remaining viewport so it never clips off-screen; overflow-y:auto
 * on Main provides internal scroll in that case.
 */
function computePlacement(triggerRect: DOMRect): Placement {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  const top = triggerRect.bottom + MARGIN;
  const maxHeight = Math.max(100, viewportH - top - MARGIN);

  const left = Math.max(
    MARGIN,
    Math.min(triggerRect.left, viewportW - WIDTH - MARGIN),
  );

  return { top, left, maxHeight };
}

const ColorPicker = (props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const [color, setColor] = useState<string>(
    getDefaultRgba(props.defaultValue),
  );

  const [defaultAlpha] = useState(
    props.solidDefaultAlpha ? '1' : getDefaultAlpha(props.defaultValue),
  );

  const [rgbaColor, setRgbaColor] = useState<RgbaColor>(
    color
      ? stringToRgba(videoCreator, color)
      : {
          r: 123,
          g: 64,
          b: 64,
          a: 100,
        },
  );
  const [selectedTab, setSelectedTab] = useState<ColorTabs>(
    props.tab || ColorTabs.preset,
  );
  const [editableColor, setEditableColor] = useState<{
    isValid: boolean;
    color: string;
  }>({
    isValid: true,
    color: '#000000',
  });
  useEffect(() => {
    if (color) {
      setEditableColor({
        isValid: true,
        color: videoCreator.textBrandingService.rgbaToHex(color).slice(0, 7),
      });
      setRgbaColor(stringToRgba(videoCreator, color));
    } else {
      // No color (e.g. reopened after erase) — leave the hex input empty rather
      // than showing the initial #000000 placeholder.
      setEditableColor({ isValid: false, color: '' });
    }
  }, []);

  const committedColorRef = useRef<string>(
    getDefaultRgba(props.defaultValue) ?? '',
  );

  // Tracks whether a hover session is active. A boolean ref is sufficient —
  // we no longer snapshot React state here because restoring from stale
  // state (captured before React flushed a click update) was the source of
  // the "reverts to old color after selecting a preset" bug. Instead, on
  // hover-end we always restore from committedColorRef, which is a ref and
  // is therefore always current even before React re-renders.
  const hoverActiveRef = useRef(false);
  // Pending revert timer — see handleSwatchLeave for why this is needed.
  const revertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHoverColor = (_color: string) => {
    // Cancel any revert scheduled by the previous swatch's onMouseLeave so
    // moving between adjacent swatches doesn't flash the committed color.
    if (revertTimerRef.current !== null) {
      clearTimeout(revertTimerRef.current);
      revertTimerRef.current = null;
    }
    hoverActiveRef.current = true;
    setColor(_color);
    setRgbaColor(stringToRgba(videoCreator, _color));
    setEditableColor({
      isValid: true,
      color: videoCreator.textBrandingService.rgbaToHex(_color).slice(0, 7),
    });
    props.onPreview?.(_color);
  };

  const handleHoverEnd = () => {
    if (hoverActiveRef.current) {
      hoverActiveRef.current = false;
      const committed = committedColorRef.current;
      if (committed) {
        setColor(committed);
        setRgbaColor(stringToRgba(videoCreator, committed));
        setEditableColor({
          isValid: true,
          color: videoCreator.textBrandingService
            .rgbaToHex(committed)
            .slice(0, 7),
        });
      }
    }
    props.onPreview?.(null);
  };

  // Called by each swatch's onMouseLeave. Schedules the revert via
  // setTimeout(0) so that moving to an adjacent swatch gives handleHoverColor
  // a chance to cancel it first — no flicker between neighbouring swatches.
  const handleSwatchLeave = () => {
    revertTimerRef.current = setTimeout(() => {
      revertTimerRef.current = null;
      handleHoverEnd();
    }, 0);
  };

  const pickColor = () => {
    const EyeDropperConstructor = window.EyeDropper;
    if (!EyeDropperConstructor) {
      logger.warn('EyeDropper is not supported');
      return;
    }
    const eyeDropper = new EyeDropperConstructor();
    (async () => {
      try {
        const color = await eyeDropper.open();
        const rgbaString = hexToRgba(color.sRGBHex);
        if (!rgbaString) return;
        setColor(rgbaString);
        const rgbaValue = stringToRgba(videoCreator, rgbaString);
        setRgbaColor(rgbaValue);
        setEditableColor({
          isValid: true,
          color: color.sRGBHex.slice(0, 7),
        });
        props.getValue(rgbaString);
        props.onClose();
      } catch (e) {
        logger.error('EyeDropper canceled or failed', e);
      }
    })();
  };

  const popoverRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement>(() =>
    computePlacement(props.pos),
  );

  useLayoutEffect(() => {
    setPlacement(computePlacement(props.pos));
  }, [props.pos]);

  // Self-managed dismissal: click-outside, scroll, resize, Escape.
  // Replaces the parent-side useOutsideAlerter callers previously needed.
  useEffect(() => {
    const triggerRect = props.pos;
    const onClose = props.onClose;
    const onPreview = props.onPreview;

    // Dismissing without selecting (Escape, click-outside, scroll, resize)
    // must discard the active hover preview and restore the committed color
    // on the canvas — otherwise the last-hovered color stays applied. The
    // commit path (preset onClick) calls onClose directly and does NOT go
    // through here, so committed selections are unaffected.
    const dismiss = () => {
      if (revertTimerRef.current !== null) {
        clearTimeout(revertTimerRef.current);
        revertTimerRef.current = null;
      }
      hoverActiveRef.current = false;
      onPreview?.(null);
      onClose();
    };

    const isInsidePopover = (target: EventTarget | null): boolean => {
      const root = popoverRef.current;
      return !!(root && target instanceof Node && root.contains(target));
    };

    const isInsideTrigger = (clientX: number, clientY: number): boolean =>
      clientX >= triggerRect.left &&
      clientX <= triggerRect.right &&
      clientY >= triggerRect.top &&
      clientY <= triggerRect.bottom;

    function onMouseDown(e: MouseEvent) {
      if (isInsidePopover(e.target)) return;
      if (isInsideTrigger(e.clientX, e.clientY)) return;
      dismiss();
    }

    function onScroll(e: Event) {
      if (isInsidePopover(e.target)) return;
      dismiss();
    }

    function onResize() {
      dismiss();
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') dismiss();
    }

    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [props.pos, props.onClose, props.onPreview]);

  const brandPrimaryColors = parseColorList(
    videoCreator.organization?.branding?.primaryColors,
  );
  const brandSecondaryColors = parseColorList(
    videoCreator.organization?.branding?.secondaryColors,
  );

  const popover = (
    <Main
      ref={popoverRef}
      // Picking a colour is not a click away from the canvas selection
      // (PLAT-12102 DS6; see floatingToolbar/ToolbarLayer).
      data-keeps-canvas-selection=""
      style={{
        top: placement.top,
        left: placement.left,
        maxHeight: placement.maxHeight,
      }}
    >
      <Tabs>
        {Object.values(ColorTabs).map(t => (
          <Tab
            onClick={() => setSelectedTab(t)}
            isSelected={selectedTab === t}
            key={t}
          >
            {t}
          </Tab>
        ))}
      </Tabs>

      <PickerContent>
        {selectedTab === ColorTabs.custom && (
          <CustomPicker>
            <RgbaColorPicker
              color={rgbaColor}
              onChange={e => {
                setRgbaColor(e);
                const _color = `rgba(${e.r}, ${e.g}, ${e.b}, ${e.a / 100})`;
                setEditableColor({
                  isValid: true,
                  color: videoCreator.textBrandingService
                    .rgbaToHex(_color)
                    .slice(0, 7),
                });
                setColor(_color);
                committedColorRef.current = _color;
                props.getValue(_color);
              }}
            />
          </CustomPicker>
        )}

        {selectedTab === ColorTabs.preset && (
          <PresetsContainer onMouseLeave={handleHoverEnd}>
            {brandPrimaryColors?.length ? (
              <ColorPickerSection
                title="Brand Kit - Primary"
                color={color}
                rgbaColor={rgbaColor}
                colors={brandPrimaryColors}
                defaultAlpha={defaultAlpha}
                setEditableColor={setEditableColor}
                setColor={setColor}
                setRgbaColor={setRgbaColor}
                getValue={_color => {
                  props.getValue(_color);
                  props.onClose();
                }}
                onPreview={c => c && handleHoverColor(c)}
                onSwatchLeave={handleSwatchLeave}
                committedColor={committedColorRef.current}
                onCommit={c => {
                  if (revertTimerRef.current !== null) {
                    clearTimeout(revertTimerRef.current);
                    revertTimerRef.current = null;
                  }
                  committedColorRef.current = c;
                  hoverActiveRef.current = false;
                }}
              />
            ) : null}
            {brandSecondaryColors?.length ? (
              <ColorPickerSection
                title="Brand Kit - Secondary"
                color={color}
                rgbaColor={rgbaColor}
                colors={brandSecondaryColors}
                defaultAlpha={defaultAlpha}
                setEditableColor={setEditableColor}
                setColor={setColor}
                setRgbaColor={setRgbaColor}
                getValue={_color => {
                  props.getValue(_color);
                  props.onClose();
                }}
                onPreview={c => c && handleHoverColor(c)}
                onSwatchLeave={handleSwatchLeave}
                committedColor={committedColorRef.current}
                onCommit={c => {
                  if (revertTimerRef.current !== null) {
                    clearTimeout(revertTimerRef.current);
                    revertTimerRef.current = null;
                  }
                  committedColorRef.current = c;
                  hoverActiveRef.current = false;
                }}
              />
            ) : null}
            <Preset>
              {brandPrimaryColors?.length || brandSecondaryColors?.length ? (
                <PresetTitle>Default Colors</PresetTitle>
              ) : null}
              <PresetPicker>
                {PRESET_COLORS.map(c => (
                  <PresetColor
                    key={c}
                    onMouseEnter={() => {
                      const _color =
                        videoCreator.textBrandingService.addAlphaToRgba(
                          c,
                          defaultAlpha,
                        );
                      handleHoverColor(_color);
                    }}
                    onMouseLeave={handleSwatchLeave}
                    onClick={() => {
                      if (revertTimerRef.current !== null) {
                        clearTimeout(revertTimerRef.current);
                        revertTimerRef.current = null;
                      }
                      const _color =
                        videoCreator.textBrandingService.addAlphaToRgba(
                          c,
                          defaultAlpha,
                        );

                      committedColorRef.current = _color;
                      hoverActiveRef.current = false;
                      setEditableColor({
                        isValid: true,
                        color: videoCreator.textBrandingService
                          .rgbaToHex(_color)
                          .slice(0, 7),
                      });

                      setColor(c);
                      setRgbaColor(stringToRgba(videoCreator, _color));
                      props.getValue(_color);
                      props.onClose();
                    }}
                    color={c}
                  >
                    {isMatchingPreset(
                      videoCreator,
                      committedColorRef.current,
                      committedColorRef.current
                        ? stringToRgba(videoCreator, committedColorRef.current)
                        : rgbaColor,
                      c,
                    ) && (
                      <CheckIcon
                        height="12"
                        width="12"
                        fillColor={
                          isLightColor(c)
                            ? 'rgba(0, 0, 0, 0.85)'
                            : 'rgba(255, 255, 255, 0.9)'
                        }
                      />
                    )}
                  </PresetColor>
                ))}
              </PresetPicker>
            </Preset>
          </PresetsContainer>
        )}

        <BottomPane>
          <Remove
            onClick={() => {
              if (revertTimerRef.current !== null) {
                clearTimeout(revertTimerRef.current);
                revertTimerRef.current = null;
              }
              committedColorRef.current = '';
              hoverActiveRef.current = false;
              setEditableColor({ isValid: false, color: '' });
              setColor('');
              props.getValue(null);
              props.onClose();
            }}
          >
            <DeleteIcon height="15px" width="15px" />
          </Remove>
          <Pick
            aria-label="Pick color from screen"
            title="Pick color from screen"
            onClick={pickColor}
          >
            <EyeDropperIcon height="15px" width="15px" />
          </Pick>

          <EditableContainer>
            <PreviewSwatch
              // No committed color (e.g. reopened after erase): rgbaColor still
              // holds its {123,64,64} initializer fallback, so render transparent
              // instead of that stray maroon. rgbaColor omitted to skip the
              // black-swatch border treatment.
              color={
                isNoneColor(color) ? 'transparent' : rgbaToString(rgbaColor)
              }
              rgbaColor={isNoneColor(color) ? undefined : rgbaColor}
            />
            <EditableInput
              maxLength={7}
              onChange={e => {
                const newColor = hexToRgba(e.target.value);
                if (newColor) {
                  const _color =
                    videoCreator.textBrandingService.addAlphaToRgba(
                      newColor,
                      defaultAlpha,
                    );
                  setColor(_color);
                  props.getValue(_color, true);
                  setEditableColor({
                    isValid: true,
                    color: e.target.value.slice(0, 7),
                  });
                  setRgbaColor(stringToRgba(videoCreator, _color));
                } else {
                  setColor('');
                  setEditableColor({
                    isValid: false,
                    color: e.target.value.slice(0, 7),
                  });
                }
              }}
              onKeyDown={e => e.key === 'Enter' && props.onClose()}
              value={editableColor.color}
            />
          </EditableContainer>
        </BottomPane>
      </PickerContent>
    </Main>
  );

  return ReactDOM.createPortal(popover, document.body);
};

const parseColorList = (colors: string | undefined) => {
  if (!colors) return [];
  return colors.split(',');
};

const ColorPickerSection = ({
  title,
  color,
  rgbaColor,
  colors,
  defaultAlpha,
  setEditableColor,
  setColor,
  setRgbaColor,
  getValue,
  onPreview,
  onSwatchLeave,
  committedColor,
  onCommit,
}: {
  title?: string;
  color: string;
  rgbaColor: RgbaColor;
  colors: string[];
  defaultAlpha?: string;
  setEditableColor: (value: { isValid: boolean; color: string }) => void;
  setColor: (value: string) => void;
  setRgbaColor: (value: RgbaColor) => void;
  getValue: (color: string | null) => void;
  onPreview?: (color: string | null) => void;
  onSwatchLeave?: () => void;
  committedColor?: string;
  onCommit?: (color: string) => void;
}) => {
  const videoCreator = useVideoCreatorStore();
  return (
    <Preset>
      {title && <PresetTitle>{title}</PresetTitle>}
      <BrandKitPicker>
        {colors.map(hex => {
          const c = hexToRgba(hex);
          if (!c) return null;
          return (
            <PresetColor
              key={c}
              onMouseEnter={() => {
                const _color = videoCreator.textBrandingService.addAlphaToRgba(
                  c,
                  defaultAlpha,
                );
                onPreview?.(_color);
              }}
              onMouseLeave={onSwatchLeave}
              onClick={() => {
                const _color = videoCreator.textBrandingService.addAlphaToRgba(
                  c,
                  defaultAlpha,
                );
                onCommit?.(_color);
                setEditableColor({
                  isValid: true,
                  color: videoCreator.textBrandingService
                    .rgbaToHex(_color)
                    .slice(0, 7),
                });
                setColor(c);
                setRgbaColor(stringToRgba(videoCreator, _color));
                getValue(_color);
              }}
              color={c}
            >
              {isMatchingPreset(
                videoCreator,
                committedColor ?? color,
                committedColor
                  ? stringToRgba(videoCreator, committedColor)
                  : rgbaColor,
                c,
              ) && (
                <CheckIcon
                  height="12"
                  width="12"
                  fillColor={
                    isLightColor(c)
                      ? 'rgba(0, 0, 0, 0.85)'
                      : 'rgba(255, 255, 255, 0.9)'
                  }
                />
              )}
            </PresetColor>
          );
        })}
      </BrandKitPicker>
    </Preset>
  );
};

export default ColorPicker;

const Main = styled.div`
  box-sizing: border-box;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--elevation-toast);
  width: ${WIDTH}px;
  /* Safety net for short viewports — inline maxHeight from JS overrides when set */
  max-height: calc(100vh - ${MARGIN * 2}px);
  overflow-y: auto;
  position: fixed;
  /* Must sit above the modal band (<Modal>=1000010, sidebar tooltips=1000020)
     because the picker opens from inside modals/sidepanels; stays below the
     global toast/refresh banners (10000001+). Do NOT lower to the 100001
     context-menu tier — that drops it behind <Modal> (e.g. Pull Quotes). */
  z-index: 9999999;
  padding: 10px;
`;

const CustomPicker = styled.div`
  & .react-colorful {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  & .react-colorful__saturation {
    border-radius: 6px;
    height: 96px;
    border: 1px solid var(--border);
  }
  & .react-colorful__saturation-pointer {
    width: 16px;
    height: 16px;
    color: var(--text-secondary);
    background-color: var(--text-secondary);
  }
  & .react-colorful__hue {
    border-radius: 20px;
    height: 16px;
  }
  & .react-colorful__hue-pointer {
    width: 8px;
    height: 8px;
    color: var(--text-secondary);
    background-color: var(--text-secondary);
    box-shadow: var(--elevation-toast);
  }
  & .react-colorful__alpha {
    width: 12px;
    height: inherit;
    border-radius: 0;
    display: none;
  }
`;

const Tabs = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const Tab = styled.div<{ isSelected: boolean }>`
  font-size: var(--text-xs);
  color: ${props =>
    props.isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
  border-bottom: 2px solid
    ${props => (props.isSelected ? 'var(--accent)' : 'var(--text-secondary)')};
  padding-bottom: 4px;
  width: 50%;
  cursor: pointer;
  transition: color var(--transition-fast);
  &:hover {
    color: ${props =>
      props.isSelected ? 'var(--accent)' : 'var(--foreground)'};
  }
`;

const PickerContent = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const PresetsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Preset = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const PresetTitle = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

/* Flat flex-wrap grid — 10 swatches per row at 20px + 5px gap = 245px */
const PresetPicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

/* Flex row used for brand-kit sections (1–few colors) */
const BrandKitPicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const PresetColor = styled.span<{ color: string; rgbaColor?: RgbaColor }>`
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  background-color: ${props => props.color};
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  transition:
    transform 0.1s ease,
    filter 0.1s ease;
  &:hover {
    transform: scale(1.1);
    filter: brightness(1.15);
  }

  ${({ rgbaColor: c }) =>
    c &&
    css`
      border: ${c.r === 0 &&
      c.b === 0 &&
      c.g === 0 &&
      '1px solid var(--border)'};
    `}

  ${({ color, rgbaColor }) =>
    color &&
    !rgbaColor &&
    css`
      border: ${color === 'rgba(0, 0, 0, 1)' && '1px solid var(--border)'};
    `}
`;

const PreviewSwatch = styled.span<{ color: string; rgbaColor?: RgbaColor }>`
  box-sizing: border-box;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background-color: ${props => props.color};
  flex-shrink: 0;

  ${({ rgbaColor: c }) =>
    c &&
    css`
      border: ${c.r === 0 &&
      c.b === 0 &&
      c.g === 0 &&
      '1px solid var(--border)'};
    `}
`;

const ActionSquare = styled.button<{ interactive?: boolean }>`
  border: 1px solid var(--border);
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  padding: 0;
  cursor: ${p => (p.interactive ? 'pointer' : 'default')};
  color: var(--text-secondary);
  flex-shrink: 0;
  &:hover {
    background: ${p =>
      p.interactive ? 'var(--surface-subtle)' : 'transparent'};
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
  }
`;
const Remove = styled(ActionSquare).attrs({ interactive: true })``;
const Pick = styled(ActionSquare).attrs({ interactive: true })``;
const BottomPane = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
`;

const EditableContainer = styled.div`
  display: flex;
  height: 28px;
  flex: 1;
  align-items: center;
  padding-left: 6px;
  border-radius: 6px;
  border: 1px solid var(--border);
  min-width: 0;
`;

const EditableInput = styled.input`
  background-color: transparent;
  color: var(--text-secondary);
  text-transform: uppercase;
  outline: 0;
  border: 0;
  flex: 1;
  min-width: 0;
  font-size: var(--text-xs);
`;
